import { refreshTokens } from "./srpLogin.js";

import {
  clearAuthTokens,
  getAuthTokens,
  storeAuthTokens,
} from "./cookieUtils.js";

const REFRESH_LOCK_KEY = "auth_refresh_lock";
const REFRESH_LOCK_TTL_MS = 15_000; // lock expires after 15s in case of crash

class TokenRefreshManager {
  constructor() {
    // If a refresh is in progress in THIS tab, everyone awaits this
    this.currentRefreshPromise = null;

    // When we last successfully refreshed (in-memory, per tab)
    this.lastRefreshAt = 0;

    // Small cooldown so we don't hammer Cognito right after a refresh (per tab)
    this.MIN_REFRESH_INTERVAL_MS = 5_000; // 5 seconds

    // Unique ID for this tab
    this.tabId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  // --- Helpers ----------------------------

  getStoredUser() {
    return getAuthTokens();
  }

  saveUser(user) {
    storeAuthTokens(user);
  }

  getTokenExpiration(token) {
    if (!token) return null;
    try {
      const [, payloadB64] = token.split(".");
      if (!payloadB64) return null;

      const payloadJson = atob(payloadB64);
      const payload = JSON.parse(payloadJson);

      return typeof payload.exp === "number" ? payload.exp : null;
    } catch {
      return null;
    }
  }

  /**
   * Decide if we SHOULD refresh.
   * Uses:
   * - cooldown (5 seconds)
   * - tokenRefreshedAt if present
   * - falls back to JWT `exp` (2 minute safety window)
   */
  needsRefresh(user, maxAgeMinutes = 50) {
    const now = Date.now();

    // Cooldown: if we *just* refreshed in this tab, don't do it again
    if (now - this.lastRefreshAt < this.MIN_REFRESH_INTERVAL_MS) {
      return false;
    }

    if (!user) return true;

    // Preferred: use `tokenRefreshedAt` if we've set it previously
    if (user.tokenRefreshedAt) {
      const ageMs = now - user.tokenRefreshedAt;
      const maxAgeMs = maxAgeMinutes * 60 * 1000;
      return ageMs > maxAgeMs;
    }

    // Fallback: use access token / id token expiry
    const token = user.accessToken || user.idToken;
    const exp = this.getTokenExpiration(token); // seconds
    if (!exp) return true;

    const expMs = exp * 1000;
    const safetyWindowMs = 2 * 60 * 1000; // refresh 2 minutes before expiry

    return expMs - now < safetyWindowMs;
  }

  // ---- Cross-tab lock helpers ------------

  getLock() {
    try {
      const raw = localStorage.getItem(REFRESH_LOCK_KEY);
      if (!raw) return null;

      const lock = JSON.parse(raw);
      if (!lock || typeof lock.ts !== "number" || !lock.tabId) return null;

      // If lock is too old, consider it stale
      if (Date.now() - lock.ts > REFRESH_LOCK_TTL_MS) {
        localStorage.removeItem(REFRESH_LOCK_KEY);
        return null;
      }

      return lock;
    } catch {
      return null;
    }
  }

  tryAcquireLock() {
    const existing = this.getLock();
    if (existing) {
      return false;
    }

    const lock = { tabId: this.tabId, ts: Date.now() };

    try {
      localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(lock));
    } catch {
      return false;
    }

    // Double-check that WE own it
    const stored = this.getLock();
    return stored && stored.tabId === this.tabId;
  }

  releaseLock() {
    const lock = this.getLock();
    if (lock && lock.tabId === this.tabId) {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  }

  waitForOtherTabRefresh(maxWaitMs = 10_000) {
    return new Promise((resolve) => {
      const start = Date.now();

      const done = () => {
        window.removeEventListener("storage", onStorage);
        resolve();
      };

      const onStorage = (e) => {
        // Another tab released the lock
        if (e.key === REFRESH_LOCK_KEY) {
          done();
        }
      };

      window.addEventListener("storage", onStorage);

      const timer = setTimeout(() => {
        // Timeout: stop waiting, we'll decide what to do next
        done();
      }, maxWaitMs);
    });
  }

  // --- Public API -------------------------

  /**
   * Ensure the user has a fresh token.
   *
   * - If token is still fresh → returns the given user (or stored user).
   * - If a refresh is already in progress in THIS tab → waits for that promise.
   * - If another tab is refreshing → waits for storage events, then returns updated user.
   * - Otherwise → becomes "leader", refreshes once, others will wait.
   */
  async ensureFreshToken(user, maxAgeMinutes = 55) {
    const currentUser = user || this.getStoredUser();

    // First check: maybe we don't need to refresh at all
    if (!this.needsRefresh(currentUser, maxAgeMinutes)) {
      return currentUser;
    }

    // If a refresh is already in-flight in THIS tab, reuse it
    if (this.currentRefreshPromise) {
      console.log("[TokenRefreshManager] Reusing in-flight refresh promise (same tab)");
      return this.currentRefreshPromise;
    }

    // Cross-tab: try to take the lock
    if (!this.tryAcquireLock()) {
      // Another tab is refreshing; wait for it to finish
      console.log("[TokenRefreshManager] Another tab is refreshing; waiting for result");
      await this.waitForOtherTabRefresh();

      // After waiting, re-read latest user from storage
      const updatedUser = this.getStoredUser();
      return updatedUser || currentUser;
    }

    // This tab is the leader, do the refresh
    console.log("[TokenRefreshManager] Starting token refresh (this tab is leader)");
    this.currentRefreshPromise = this._refreshOnce(currentUser);

    try {
      const refreshedUser = await this.currentRefreshPromise;
      return refreshedUser;
    } finally {
      this.currentRefreshPromise = null;
      this.releaseLock();
    }
  }

  /**
   * Force an immediate refresh regardless of age.
   * Useful for "Refresh Now" buttons, or login flows.
   * Also uses cross-tab lock to avoid reuse errors.
   */
  async refreshUserToken(user) {
    const currentUser = user || this.getStoredUser();

    if (this.currentRefreshPromise) {
      return this.currentRefreshPromise;
    }

    // Same cross-tab logic as ensureFreshToken
    if (!this.tryAcquireLock()) {
      console.log("[TokenRefreshManager] Another tab is refreshing; waiting for result");
      await this.waitForOtherTabRefresh();
      const updatedUser = this.getStoredUser();
      return updatedUser || currentUser;
    }

    this.currentRefreshPromise = this._refreshOnce(currentUser);

    try {
      const refreshedUser = await this.currentRefreshPromise;
      return refreshedUser;
    } finally {
      this.currentRefreshPromise = null;
      this.releaseLock();
    }
  }

  // --- Internal refresh logic -------------

  async _refreshOnce(user) {
    // Always prefer whatever is in storage (in case another tab updated just before lock)
    const storedUser = this.getStoredUser();
    const effectiveUser = storedUser || user;

    if (!effectiveUser?.refreshToken) {
      this._handleRefreshFailure(
        new Error("Missing refresh token; user must sign in again.")
      );
    }

    // Check internet connectivity before attempting refresh
    if (!navigator.onLine) {
      console.log("[TokenRefreshManager] Skipping token refresh - no internet connection");
      // Return current user without refreshing - don't trigger logout for network issues
      return effectiveUser;
    }

    try {
      const refreshed = await refreshTokens({
        region: process.env.REACT_APP_AWS_REGION,
        clientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
        refreshToken: effectiveUser.refreshToken,
      });

      const updatedUser = {
        ...effectiveUser,
        idToken: refreshed.idToken,
        accessToken: refreshed.accessToken,
        // Some pools rotate the refresh token, some don't
        refreshToken: refreshed.refreshToken || effectiveUser.refreshToken,
        tokenRefreshedAt: Date.now(),
      };

      this.saveUser(updatedUser);
      this.lastRefreshAt = Date.now();
      window.dispatchEvent(
        new CustomEvent("userStateUpdate", { detail: { user: updatedUser } })
      );

      return updatedUser;
    } catch (err) {
      this._handleRefreshFailure(err);
    }
  }

  _handleRefreshFailure(error) {
    console.error("Token refresh failed:", error);
  
    // --- 1. Try to understand what kind of error this is ---
  
    // Axios-style shape: error.response.status / error.response.data
    const hasHttpResponse = !!error?.response;
    const status = error?.response?.status;
    const data = error?.response?.data;
  
    // Pull an error code/description out of the body if possible
    const bodyError =
      (typeof data === "string" ? data : data?.error || data?.error_description) ||
      "";
  
    // Heuristic: refresh token is truly invalid/expired/revoked
    const isInvalidRefreshToken =
      (status === 400 || status === 401) &&
      typeof bodyError === "string" &&
      /invalid[_ ]?(grant|token)|expired|refresh token/i.test(bodyError);
  
    // If there's NO HTTP response at all, this is almost certainly a network issue
    // (request never made it to Cognito or failed in transit)
    const noHttpResponse = !hasHttpResponse;
  
    // Extra safety: treat some obvious network-ish situations as transient too
    const message = error?.message || "";
    const code = error?.code || "";
  
    const looksLikeNetworkErrorByMessageOrCode =
      message.includes("Network Error") ||
      message.includes("Failed to fetch") ||
      message.includes("ERR_NETWORK_IO_SUSPENDED") ||
      message.includes("Network I/O suspended") ||
      message.includes("network I/O suspended") ||
      code === "ERR_NETWORK_IO_SUSPENDED" ||
      message.includes("ERR_NAME_NOT_RESOLVED") ||
      message.includes("Name not resolved") ||
      code === "ERR_NAME_NOT_RESOLVED" ||
      message.includes("ERR_NETWORK_CHANGED") ||
      message.includes("Network changed") ||
      code === "ERR_NETWORK_CHANGED" ||
      message.includes("ERR_INTERNET_DISCONNECTED") ||
      message.includes("Internet disconnected") ||
      code === "ERR_INTERNET_DISCONNECTED";
  
    // Some status codes are clearly transient server issues, not bad tokens
    const looksLikeServerIssueStatus =
      status === 500 || status === 502 || status === 503 || status === 504;
  
    const isNetworkError =
      noHttpResponse || looksLikeNetworkErrorByMessageOrCode;
  
    const isTransientServerError =
      hasHttpResponse && looksLikeServerIssueStatus;
  
    // --- 2. Decide what to do ---
  
    if (isNetworkError || isTransientServerError) {
      // 🔹 Case A: transient / infrastructure problem
      // -> Do NOT clear localStorage. The refresh token is almost certainly still valid.
      console.log(
        "[TokenRefreshManager] Transient network/server problem - preserving session, will retry later:",
        message || status
      );
  
      const networkError = new Error(
        "Network or server temporarily unavailable. Your session is still active."
      );
      networkError.code = "NETWORK_ERROR";
      networkError.originalError = error;
      throw networkError;
    }
  
    if (isInvalidRefreshToken) {
      // 🔹 Case B: Cognito explicitly told us the refresh token is invalid/expired
      console.log("[TokenRefreshManager] Refresh token invalid/expired, clearing session");
  
      clearAuthTokens();
      localStorage.removeItem(REFRESH_LOCK_KEY);
  
      const authError = new Error(
        "Your session has expired. Please sign in again."
      );
      authError.code = "SESSION_EXPIRED";
      authError.originalError = error;
      throw authError;
    }
  
    // 🔹 Case C: Some other HTTP error (e.g. 404 on token endpoint, misconfig, etc.)
    // It’s safer NOT to nuke the user’s session here, because it might be a backend/config issue.
    console.log(
      "[TokenRefreshManager] Unexpected token refresh error (not invalid token, not pure network). Preserving session.",
      { status, bodyError, code }
    );
  
    const genericError = new Error(
      "Could not refresh session due to an unexpected error. Your session is still active."
    );
    genericError.code = "TOKEN_REFRESH_ERROR";
    genericError.originalError = error;
    throw genericError;
  }
  
}

export const tokenRefreshManager = new TokenRefreshManager();
